import React, { useRef } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { AccessTimeFilledOutlined, Check, Clear, Add } from "@mui/icons-material";
import { mobiliTheme } from "../../../themes/mobiliTheme";

interface WeekdayAvailabilityProps {
  dayData: {
    availability: "available" | "partial" | "unavailable";
    startTime?: string;
    selected: boolean;
  };
  weekDay: string;
  onChange: (newStatus: "available" | "partial" | "unavailable", anchorEl?: HTMLElement) => void;
  disabled?: boolean;
  unavailableTooltip?: string;
  // onSetHours: (e: React.MouseEvent<HTMLElement>) => void;
  // onOpenPopup: (anchorEl: HTMLElement) => void;
}

const WeekdayAvailability: React.FC<WeekdayAvailabilityProps> = ({ dayData, weekDay, onChange, disabled = false, unavailableTooltip }) => {
  const { availability, startTime, selected } = dayData;
  const iconButtonRef = useRef<HTMLButtonElement>(null);

  // this is for the large icon that can also be clicked to change the availability for the day
  const cycleThroughStates = (e: React.MouseEvent<HTMLElement>) => {
    switch (availability) {
      case "available":
        onChange("unavailable");
        break;
      case "unavailable":
        onChange("partial", e.currentTarget);
        break;
      case "partial":
        onChange("partial", e.currentTarget);
        break;
      default:
        onChange("available");
        break;
    }
  };

  // Inline tooltip text and icon for the large icon
  let tooltipText =
    availability === "available"
      ? "Der Patient ist verfügbar."
      : availability === "unavailable"
      ? "Der Patient ist nicht verfügbar."
      : "Der Patient ist nur eingeschränkt verfügbar.";

  // If not in viewMode and partial, show unavailable slots
  if (availability === "partial" && unavailableTooltip) {
    tooltipText += ` ${unavailableTooltip}`;
  }

  const largeIcon =
    availability === "available" ? (
      <Check sx={{ fontSize: 46, mt: 0, color: mobiliTheme.palette.success.main }} />
    ) : availability === "unavailable" ? (
      <Clear sx={{ fontSize: 46, mt: 0, color: mobiliTheme.palette.error.dark }} />
    ) : (
      <AccessTimeFilledOutlined sx={{ fontSize: 42, mt: 0, color: mobiliTheme.palette.info.light }} />
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        // height: 35,
        position: "relative",
        margin: 0,
        padding: 0,
      }}
    >
      <Typography variant="body2" sx={{ position: "absolute", top: 0, left: 2, fontSize: '0.75rem', margin: 0, padding: 0 }}>{weekDay}</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, mt: 2.5, ml: 0, mr: disabled ? 0 : 2 }}>
        <Tooltip title={tooltipText} placement="top" disableInteractive>
          <IconButton ref={iconButtonRef} onClick={cycleThroughStates} sx={{ p: 0, cursor: disabled ? 'default' : 'pointer' }}>
            {largeIcon}
          </IconButton>
        </Tooltip>
      </Box>

      {!disabled && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, position: "absolute", right: 0, top: 0, }}>
          <Tooltip title="Verfügbar" placement="left" disableInteractive>
            <IconButton
              onClick={() => onChange("available")}
              size="small"
              sx={{
                p: 0,
                opacity: 0.4,
                transition: "opacity 0.2s",
                '&:hover': { opacity: 1 },
              }}
            >
              <Check sx={{ fontSize: 17, color: availability === "available" ? mobiliTheme.palette.success.main : "default" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Nicht verfügbar" placement="left" disableInteractive>
            <IconButton
              onClick={() => onChange("unavailable")}
              size="small"
              sx={{
                p: 0,
                opacity: 0.4,
                transition: "opacity 0.2s",
                '&:hover': { opacity: 1 },
              }}
            >
              <Clear sx={{ fontSize: 17, color: availability === "unavailable" ? mobiliTheme.palette.error.dark : "default" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eingeschränkt verfügbar" placement="left" disableInteractive>
            <IconButton
              onClick={() => onChange("partial")}
              size="small"
              sx={{
                p: 0,
                opacity: 0.4,
                transition: "opacity 0.2s",
                '&:hover': { opacity: 1 },
              }}
            >
              <AccessTimeFilledOutlined sx={{ fontSize: 17, color: availability === "partial" ? mobiliTheme.palette.info.light : "default" }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}  
    </Box>
  );
};

export default WeekdayAvailability;
